import { xEmbedForm } from "./x-embed-form";
import { XEmbedListener } from "./x-embed-listener";

type TwitterWidgetsApi = {
    widgets?: {
        load: (element?: Element | null) => void;
    };
    ready?: (callback: (api: TwitterWidgetsApi) => void) => void;
};

declare global {
    interface Window {
        twttr?: TwitterWidgetsApi;
    }
}

let widgetsScriptPromise: Promise<TwitterWidgetsApi | null> | null = null;

function normalizeXPermalink(url: string): string {
    const value = String(url || "").trim();
    if (!value) return "";

    try {
        const parsed = new URL(value);
        const host = parsed.hostname.toLowerCase();
        if (host === "twitter.com" || host === "www.twitter.com") {
            parsed.hostname = "x.com";
        }
        return parsed.toString();
    } catch {
        return value.replace(/https?:\/\/(?:www\.)?twitter\.com/i, "https://x.com");
    }
}

function loadWidgetsScript(): Promise<TwitterWidgetsApi | null> {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return Promise.resolve(null);
    }

    if (window.twttr?.widgets?.load) {
        return Promise.resolve(window.twttr);
    }

    if (widgetsScriptPromise) {
        return widgetsScriptPromise;
    }

    widgetsScriptPromise = new Promise((resolve) => {
        const existingScript = xEmbedForm.getEmbedScript(document);
        if (existingScript) {
            if (existingScript.dataset.xEmbedReady === "1") {
                resolve(window.twttr || null);
                return;
            }

            if (existingScript.dataset.xEmbedFailed === "1") {
                resolve(null);
                return;
            }

            XEmbedListener.bindScriptLoad(existingScript, () => resolve(window.twttr || null));
            XEmbedListener.bindScriptError(existingScript, () => resolve(null));
            return;
        }

        const script = document.createElement("script");
        script.src = "https://platform.twitter.com/widgets.js";
        script.async = true;
        script.charset = "utf-8";
        script.setAttribute("data-role", "x-embed-widgets");
        XEmbedListener.bindScriptLoad(script, () => {
            script.dataset.xEmbedReady = "1";
            resolve(window.twttr || null);
        });
        XEmbedListener.bindScriptError(script, () => {
            script.dataset.xEmbedFailed = "1";
            resolve(null);
        });
        document.head.appendChild(script);
    });

    return widgetsScriptPromise;
}

export function buildXEmbedNode(url: string): HTMLDivElement {
    const permalink = normalizeXPermalink(url);
    const wrapper = document.createElement("div");
    wrapper.className = "sns-embed sns-embed--x";

    const blockquote = document.createElement("blockquote");
    blockquote.className = "twitter-tweet";
    blockquote.setAttribute("data-dnt", "true");
    blockquote.setAttribute("data-theme", "light");
    blockquote.setAttribute("data-cards", "hidden");
    blockquote.style.margin = "0";

    const anchor = document.createElement("a");
    anchor.href = permalink;
    anchor.textContent = permalink;

    blockquote.appendChild(anchor);
    wrapper.appendChild(blockquote);

    return wrapper;
}

export function ensureXEmbedWidgetsLoadedAndRefresh(target?: Element | null): void {
    void loadWidgetsScript().then((api) => {
        const load = api?.widgets?.load;
        if (typeof load === "function") {
            load(target || undefined);
        }
    });
}
